import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabaseClient'
import { Target, Clock, CheckCircle, XCircle, Crosshair, X, ChevronDown, ChevronRight, Pencil, Check } from 'lucide-react'
import { isDevMode } from '../lib/devMode'
import { isContentEditable } from '../lib/eventPhase'
import CountdownTimer from './CountdownTimer'

const C = {
  display: { fontFamily: "'VT323', monospace", letterSpacing: '0.08em', textTransform: 'uppercase' },
  mono: { fontFamily: "'JetBrains Mono', monospace" },
  phos: { color: '#00ff66' },
  dim: { color: '#4aaa5a' },
  deep: { color: '#2a6a3a' },
  amber: { color: '#ffcc33' },
  alert: { color: '#ff2a2a' },
  panel: { background: 'rgba(0, 20, 8, .4)', border: '1px solid rgba(0,255,102,.18)', boxShadow: '0 0 6px rgba(0,255,102,.08)', position: 'relative' },
  tag: { fontFamily: "'JetBrains Mono', monospace", fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '2px 8px', textShadow: 'none' },
}

const statusEdge = { active: '#00ff66', claimed: '#4aaa5a', expired: '#2a6a3a', cancelled: '#ff2a2a', queued: '#ffcc33' }
const statusTag = {
  claimed: { background: 'rgba(0,255,102,.08)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.2)' },
  expired: { background: 'rgba(0,255,102,.04)', color: '#2a6a3a', border: '1px solid rgba(0,255,102,.1)' },
  cancelled: { background: 'rgba(255,42,42,.08)', color: '#ff2a2a', border: '1px solid rgba(255,42,42,.2)' },
}

function Brackets({ children, style = {} }) {
  return (
    <div style={{ ...C.panel, ...style }}>
      <div style={{ position:'absolute',top:-1,left:-1,width:8,height:8,borderTop:'2px solid #4aaa5a',borderLeft:'2px solid #4aaa5a' }}/>
      <div style={{ position:'absolute',bottom:-1,right:-1,width:8,height:8,borderBottom:'2px solid #4aaa5a',borderRight:'2px solid #4aaa5a' }}/>
      {children}
    </div>
  )
}

export default function BountyBoard({ event, bounties, onRefresh, user, userFactionId, isGm, factions = [], players = [], hideScores = false, isPaused = false }) {
  // UX-1 — pre-live events allow inline edits on bounty content. Once the
  // event flips to live the GM can still cancel via the existing controls
  // but mission_name / points / notes are frozen.
  const canEditContent = isGm && isContentEditable(event?.status)
  const [expanded, setExpanded] = useState({})
  const [editingId, setEditingId] = useState(null)
  const [editDraft, setEditDraft] = useState({ title: '', mission_name: '', points: '', notes: '' })

  const startEdit = (b) => {
    setEditingId(b.id)
    setEditDraft({
      title: b.title || '',
      mission_name: b.mission_name || '',
      points: b.points ?? '',
      notes: b.notes || '',
    })
    setExpanded(e => ({ ...e, [b.id]: true }))
  }
  const saveEdit = async (id) => {
    const ptsNum = editDraft.points === '' ? null : parseInt(editDraft.points)
    const { error } = await supabase.from('bounties').update({
      title: editDraft.title.trim() || null,
      mission_name: editDraft.mission_name.trim() || null,
      points: ptsNum,
      notes: editDraft.notes.trim() || null,
    }).eq('id', id)
    if (error) { alert('Save failed: ' + error.message); return }
    setEditingId(null)
    onRefresh()
  }
  const [pursuits, setPursuits] = useState([])
  // Map user_id → faction_id from the event roster. Used (GM view only) to
  // bucket bounties by target faction so the GM gets a faction-grouped read
  // of the board instead of one giant list.
  const playerFactionMap = useMemo(() => {
    const m = {}
    ;(players || []).forEach(p => { m[p.user_id] = p.faction_id })
    return m
  }, [players])

  // Stable key derived from the SET of bounty IDs. Parent (GameEvent) polls
  // every 30s and sets bounties to a fresh array each time, which would
  // otherwise re-fire the pursuits fetch on every poll cycle even when the
  // bounty IDs haven't changed. This memo keeps the effect quiet until the
  // actual ID set changes.
  const bountyIdsKey = useMemo(
    () => bounties.map(b => b.id).sort().join(','),
    [bounties]
  )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (bounties.length > 0) { fetchPursuits(); fetchStackLogs() } }, [bountyIdsKey])

  const fetchPursuits = async () => { const { data } = await supabase.from('bounty_pursuits').select('*, users(display_name, callsign)').in('bounty_id', bounties.map(b => b.id)); setPursuits(data || []) }
  const fetchStackLogs = async () => { const { data } = await supabase.from('bounty_stack_log').select('*, added_by_user:users!added_by(callsign, display_name)').in('bounty_id', bounties.map(b => b.id)).order('created_at', { ascending: true }); setStackLogs(data || []) }
  const commitToBounty = async (bounty) => { await supabase.from('bounty_pursuits').insert({ bounty_id: bounty.id, user_id: user.id, faction_id: userFactionId }); fetchPursuits() }
  const standDown = async (bountyId) => { await supabase.from('bounty_pursuits').delete().eq('bounty_id', bountyId).eq('user_id', user.id); fetchPursuits() }
  const cancelBounty = async (bountyId) => { await supabase.from('bounties').update({ status: 'cancelled' }).eq('id', bountyId); onRefresh() }
  const activateBounty = async (bountyId) => { await supabase.from('bounties').update({ status: 'active' }).eq('id', bountyId); onRefresh() }
  const devClaim = async (bountyId, points, targetId) => { const { data: { user: u } } = await supabase.auth.getUser(); await supabase.from('bounties').update({ status: 'claimed', claimed_by: u.id, claimed_at: new Date().toISOString() }).eq('id', bountyId); await supabase.rpc('record_kill_stats', { p_killer_id: u.id, p_target_id: targetId, p_points: points }); onRefresh() }

  const activeBounties = bounties.filter(b => b.status === 'active')
  const queuedBounties = bounties.filter(b => b.status === 'queued')
  const resolvedBounties = bounties.filter(b => !['active', 'queued', 'cancelled'].includes(b.status))
  const cancelledBounties = bounties.filter(b => b.status === 'cancelled')
  const [activeCollapsed, setActiveCollapsed] = useState(() => localStorage.getItem('dc_bounties_active_collapsed') === 'true')
  const [queuedCollapsed, setQueuedCollapsed] = useState(() => localStorage.getItem('dc_bounties_queued_collapsed') === 'true')
  // Bug 60 — resolved (closed/failed/completed) starts collapsed so the
  // active board sits front-and-center. User's explicit choice persists.
  const [resolvedCollapsed, setResolvedCollapsed] = useState(() => localStorage.getItem('dc_bounties_resolved_collapsed') !== 'false')
  const [cancelledCollapsed, setCancelledCollapsed] = useState(() => localStorage.getItem('dc_bounties_cancelled_collapsed') !== 'false')
  const toggleSection = (s) => { const m = { active: [activeCollapsed, setActiveCollapsed, 'dc_bounties_active_collapsed'], queued: [queuedCollapsed, setQueuedCollapsed, 'dc_bounties_queued_collapsed'], resolved: [resolvedCollapsed, setResolvedCollapsed, 'dc_bounties_resolved_collapsed'], cancelled: [cancelledCollapsed, setCancelledCollapsed, 'dc_bounties_cancelled_collapsed'] }; const [c, set, k] = m[s]; const n = !c; set(n); localStorage.setItem(k, String(n)) }

  const [stackLogs, setStackLogs] = useState([])
  const [expandedStack, setExpandedStack] = useState({})

  if (bounties.length === 0) return (
    <div className="text-center py-16" style={C.deep}>
      <Target size={48} className="mx-auto mb-4 opacity-50" />
      <p style={C.display}>NO BOUNTIES ISSUED</p>
      <p className="text-sm mt-1" style={C.deep}>Start the event and issue a bounty to begin</p>
    </div>
  )

  // ANU-36: rendered as a plain function (lowercase), not a React component.
  // When this was `const BountyCard = (...) => ...` defined inside the
  // BountyBoard body, every parent re-render produced a new function
  // reference; React reconciled `<BountyCard />` as a new component type
  // and unmounted the input subtree on every keystroke, dropping focus.
  // Calling it as `renderBountyCard(b)` returns raw JSX whose element
  // types (Brackets, div, input) are stable across renders, so the inline
  // edit inputs keep their DOM identity and focus.
  const renderBountyCard = (bounty) => {
    const bp = pursuits.filter(p => p.bounty_id === bounty.id)
    const bStackLogs = stackLogs.filter(l => l.bounty_id === bounty.id)
    const myP = bp.find(p => p.user_id === user?.id)
    const isTargetFaction = bounty.target_id && userFactionId && bounties.find(b => b.id === bounty.id)?.target?.faction_id === userFactionId
    const canSeePursuits = isGm || (!isTargetFaction && userFactionId === bounty.issuer_faction_id)
    const canPursue = bounty.status === 'active' && user && !isTargetFaction
    const edge = statusEdge[bounty.status] || '#2a6a3a'
    const isExpanded = !!expanded[bounty.id]
    const isEditing = editingId === bounty.id

    return (
      <Brackets key={bounty.id} style={{ padding: '12px 14px', marginBottom: '6px', borderLeft: `3px solid ${edge}` }}>
        <div className="flex justify-between items-start gap-2">
          <div className="min-w-0 flex-1">
            <button onClick={() => setExpanded(e => ({ ...e, [bounty.id]: !isExpanded }))}
              className="flex items-center gap-2 mb-1 text-left w-full"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0 }}>
              {isExpanded ? <ChevronDown size={11} style={C.dim} /> : <ChevronRight size={11} style={C.dim} />}
              <Target size={13} style={C.phos} />
              <span style={{ ...C.display, fontSize: '14px' }}>{bounty.target?.callsign || bounty.target?.display_name || bounty.target?.email}</span>
            </button>
            <p className="text-xs" style={C.dim}>{bounty.title || 'KILL'} — {bounty.target?.callsign || bounty.target?.display_name || `B${bounty.id.slice(0,3).toUpperCase()}`}</p>
            <p className="text-xs" style={C.deep}>by {bounty.issuer?.callsign || bounty.issuer?.display_name || bounty.issuer?.email}</p>
            {bounty.expires_at && (() => {
              // ANU-80 — active rows show a live MM:SS countdown via the
              // shared CountdownTimer (ticks itself). Resolved rows still
              // surface the total duration for the AAR / dossier read.
              const isActive = bounty.status === 'active'
              const expired = new Date(bounty.expires_at).getTime() <= Date.now()
              const totalMs = new Date(bounty.expires_at) - new Date(bounty.issued_at)
              return (
                <p className="text-xs mt-1 flex items-center gap-1" style={isActive ? (expired ? C.alert : C.amber) : C.deep}>
                  <Clock size={10} />
                  {isActive
                    ? <CountdownTimer expiresAt={bounty.expires_at} isPaused={isPaused} />
                    : `${Math.round(totalMs / 60000)} min`}
                </p>
              )
            })()}
            {bounty.notes && <p className="text-xs mt-1 italic" style={C.deep}>"{bounty.notes}"</p>}
            {bounty.status === 'claimed' && bounty.claimed_at && (
              <p className="text-xs mt-1" style={C.phos}>Claimed by {bounty.claimer?.callsign || bounty.claimer?.display_name || 'unknown'} · {new Date(bounty.claimed_at).toLocaleTimeString()}</p>
            )}
            {bounty.status === 'active' && (canSeePursuits || isGm) && bp.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {bp.map(p => (
                  <span key={p.id} className="text-xs px-2 py-0.5 flex items-center gap-1" style={{ background: 'rgba(0,255,102,.08)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.2)' }}>
                    <Crosshair size={9} /> {p.users?.callsign || p.users?.display_name}
                  </span>
                ))}
              </div>
            )}
            {bounty.status === 'active' && !canSeePursuits && !isGm && !isTargetFaction && bp.length > 0 && (
              <p className="text-xs mt-1 flex items-center gap-1" style={C.dim}><Crosshair size={10} /> {bp.length} pursuing</p>
            )}
            {bounty.status !== 'active' && bounty.status !== 'queued' && (
              <span className="inline-block mt-2" style={{ ...C.tag, ...(statusTag[bounty.status] || statusTag.expired) }}>{bounty.status}</span>
            )}
          </div>
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            {/* Point value hidden when org sets hide_faction_scores AND viewer
                isn't a full GM. The bounty itself stays visible — players
                can pursue without seeing the carrot's weight. */}
            {hideScores ? (
              <span style={{ ...C.display, fontSize: '12px', color: '#4aaa5a', letterSpacing: '0.14em' }}>BOUNTY</span>
            ) : (
              <span style={{ ...C.display, fontSize: '20px', color: '#ffcc33' }}>{bounty.points} <span style={{ ...C.deep, fontSize: '10px' }}>pts</span></span>
            )}
            <div className="flex items-center gap-1">
              {canPursue && (myP ? (
                <button onClick={() => standDown(bounty.id)} className="flex items-center gap-1 text-xs px-2 py-1" style={{ background: 'rgba(255,42,42,.1)', color: '#ff2a2a', border: '1px solid rgba(255,42,42,.2)', ...C.display, fontSize: '11px', cursor: 'pointer' }}><X size={11} /> STAND DOWN</button>
              ) : (
                <button onClick={() => commitToBounty(bounty)} className="flex items-center gap-1 text-xs px-2 py-1" style={{ background: 'rgba(0,255,102,.06)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.18)', ...C.display, fontSize: '11px', cursor: 'pointer' }}><Crosshair size={11} /> PURSUE</button>
              ))}
              {bounty.status === 'queued' && isGm && !bounty.parent_frago_id && (
                <button onClick={() => activateBounty(bounty.id)} className="flex items-center gap-1 text-xs px-2 py-1" style={{ background: 'rgba(0,255,102,.12)', color: '#00ff66', border: '1px solid rgba(0,255,102,.4)', ...C.display, fontSize: '11px', cursor: 'pointer', textShadow: '0 0 4px rgba(0,255,102,.3)' }}>▶ ACTIVATE</button>
              )}
              {(bounty.status === 'active' || bounty.status === 'queued') && isGm && !bounty.parent_frago_id && (
                <button onClick={() => cancelBounty(bounty.id)} style={{ background: 'none', border: 'none', color: '#2a6a3a', cursor: 'pointer' }}><XCircle size={18} /></button>
              )}
              {bounty.parent_frago_id && (bounty.status === 'active' || bounty.status === 'queued') && (
                <span style={{ ...C.mono, fontSize: '9px', color: '#4aaa5a', padding: '2px 6px', border: '1px solid rgba(0,255,102,.15)', letterSpacing: '0.08em', textTransform: 'uppercase' }} title="State controlled by parent FRAGO resolution">⛓ LINKED</span>
              )}
              {bounty.status === 'active' && isDevMode && (
                <button onClick={() => devClaim(bounty.id, bounty.points, bounty.target_id)} className="text-xs px-2 py-1" style={{ background: 'rgba(77,160,255,.1)', color: '#4da0ff', border: '1px solid rgba(77,160,255,.2)', cursor: 'pointer' }}>Dev</button>
              )}
              {bounty.status === 'claimed' && <CheckCircle size={18} style={C.phos} />}
            </div>
          </div>
        </div>
        {isExpanded && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(0,255,102,.1)' }}>
            {isEditing ? (
              <div className="grid gap-2" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', display: 'block', marginBottom: 2 }}>TITLE (optional)</label>
                  <input value={editDraft.title} onChange={e => setEditDraft(d => ({ ...d, title: e.target.value }))} maxLength={80}
                    style={{ width: '100%', background: 'rgba(0,20,8,.6)', color: '#00ff66', border: '1px solid rgba(0,255,102,.3)', padding: '6px 10px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", outline: 'none' }} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', display: 'block', marginBottom: 2 }}>MISSION NAME</label>
                  <input value={editDraft.mission_name} onChange={e => setEditDraft(d => ({ ...d, mission_name: e.target.value }))} maxLength={120}
                    style={{ width: '100%', background: 'rgba(0,20,8,.6)', color: '#00ff66', border: '1px solid rgba(0,255,102,.3)', padding: '6px 10px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", outline: 'none' }} />
                </div>
                <div>
                  <label style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', display: 'block', marginBottom: 2 }}>POINTS</label>
                  <input type="number" min={0} max={10000} value={editDraft.points} onChange={e => setEditDraft(d => ({ ...d, points: e.target.value }))}
                    style={{ width: '100%', background: 'rgba(0,20,8,.6)', color: '#00ff66', border: '1px solid rgba(0,255,102,.3)', padding: '6px 10px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", outline: 'none' }} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', display: 'block', marginBottom: 2 }}>NOTES</label>
                  <textarea value={editDraft.notes} onChange={e => setEditDraft(d => ({ ...d, notes: e.target.value }))} rows={2} maxLength={500}
                    style={{ width: '100%', background: 'rgba(0,20,8,.6)', color: '#00ff66', border: '1px solid rgba(0,255,102,.3)', padding: '6px 10px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", outline: 'none', resize: 'vertical' }} />
                </div>
                <div style={{ gridColumn: '1 / -1' }} className="flex gap-2">
                  <button onClick={() => saveEdit(bounty.id)} className="flex items-center gap-1 text-xs px-2 py-1"
                    style={{ background: '#00ff66', color: '#000', border: 'none', cursor: 'pointer', ...C.display, fontSize: 11 }}><Check size={11} /> SAVE</button>
                  <button onClick={() => setEditingId(null)} className="flex items-center gap-1 text-xs px-2 py-1"
                    style={{ background: 'rgba(0,20,8,.4)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.18)', cursor: 'pointer', ...C.mono, fontSize: 11 }}><X size={11} /> CANCEL</button>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                {bounty.title && <p style={{ ...C.mono, fontSize: 11, color: '#9aff9a' }}><span style={C.dim}>TITLE:</span> {bounty.title}</p>}
                {bounty.mission_name && <p style={{ ...C.mono, fontSize: 11, color: '#9aff9a' }}><span style={C.dim}>MISSION:</span> {bounty.mission_name}</p>}
                {bounty.category && bounty.category !== 'standard' && <p style={{ ...C.mono, fontSize: 11, color: C.deep.color }}><span style={C.dim}>CATEGORY:</span> {bounty.category}</p>}
                {bounty.contact_bonus > 0 && <p style={{ ...C.mono, fontSize: 11, color: '#ffcc33' }}><span style={C.dim}>CONTACT BONUS:</span> +{bounty.contact_bonus} pts</p>}
                {bounty.current_possession && <p style={{ ...C.mono, fontSize: 11, color: '#9aff9a' }}><span style={C.dim}>POSSESSION:</span> {bounty.current_possession}</p>}
                {bounty.collection_point && <p style={{ ...C.mono, fontSize: 11, color: '#9aff9a' }}><span style={C.dim}>COLLECTION:</span> {bounty.collection_point}</p>}
                {bounty.friendly_destination && <p style={{ ...C.mono, fontSize: 11, color: '#9aff9a' }}><span style={C.dim}>FRIENDLY DEST:</span> {bounty.friendly_destination}</p>}
                {bounty.hostile_destination && <p style={{ ...C.mono, fontSize: 11, color: '#9aff9a' }}><span style={C.dim}>HOSTILE DEST:</span> {bounty.hostile_destination}</p>}
                {(bounty.prep_duration || bounty.active_duration || bounty.bleedout_duration) && (
                  <p style={{ ...C.mono, fontSize: 11, color: C.deep.color }}>
                    <span style={C.dim}>TIMERS:</span> prep {bounty.prep_duration || 0}m · active {bounty.active_duration || 0}m · bleedout {bounty.bleedout_duration || 0}m
                  </p>
                )}
                {bounty.phase && <p style={{ ...C.mono, fontSize: 11, color: C.deep.color }}><span style={C.dim}>PHASE:</span> {bounty.phase}</p>}
                <div className="flex gap-2 mt-2">
                  {canEditContent && (
                    <button onClick={() => startEdit(bounty)} className="flex items-center gap-1 text-xs px-2 py-1"
                      style={{ background: 'rgba(0,20,8,.4)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.18)', cursor: 'pointer', ...C.mono, fontSize: 11 }}><Pencil size={11} /> EDIT</button>
                  )}
                  <a href={`/bounty/${bounty.id}`} className="flex items-center gap-1 text-xs px-2 py-1"
                    style={{ background: 'rgba(0,20,8,.4)', color: '#4aaa5a', border: '1px solid rgba(0,255,102,.18)', cursor: 'pointer', ...C.mono, fontSize: 11, textDecoration: 'none' }}>DOSSIER →</a>
                </div>
                {bStackLogs.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <button onClick={() => setExpandedStack(s => ({ ...s, [bounty.id]: !s[bounty.id] }))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                      <span style={{ ...C.mono, fontSize: 10, color: '#ffcc33' }}>
                        ↑ Point value raised {bStackLogs.length} time{bStackLogs.length !== 1 ? 's' : ''} {expandedStack[bounty.id] ? '▲' : '▼'}
                      </span>
                    </button>
                    {expandedStack[bounty.id] && (
                      <div className="mt-1 space-y-1">
                        {bStackLogs.map(log => (
                          <p key={log.id} style={{ ...C.mono, fontSize: 10, color: '#4aaa5a', paddingLeft: 8 }}>
                            +{log.points_added} pts{log.reason ? ` · "${log.reason}"` : ''}
                            <span style={C.deep}> — {log.added_by_user?.callsign || 'GM'} · {new Date(log.created_at).toLocaleTimeString()}</span>
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Brackets>
    )
  }

  const SectionHeader = ({ label, count, collapsed, section, color = '#00ff66' }) => (
    <button onClick={() => toggleSection(section)} className="w-full flex items-center justify-between mb-3" style={{ background: 'none', border: 'none', cursor: 'pointer', color }}>
      <span style={{ ...C.mono, fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{label} — {count}</span>
      <span style={{ fontSize: '10px', color: '#2a6a3a' }}>{collapsed ? '▼ show' : '▲ hide'}</span>
    </button>
  )

  // Group bounties by target faction (GM read of the board). Bounties whose
  // target has no faction or whose target_id is null land under ALL FACTIONS.
  const groupByFaction = (list) => {
    const groups = new Map()  // faction_id → []
    const ungrouped = []
    list.forEach(b => {
      const fid = b.target_id ? playerFactionMap[b.target_id] : null
      if (fid) {
        if (!groups.has(fid)) groups.set(fid, [])
        groups.get(fid).push(b)
      } else {
        ungrouped.push(b)
      }
    })
    const ordered = factions
      .filter(f => groups.has(f.id))
      .map(f => ({ faction: f, items: groups.get(f.id) }))
    if (ungrouped.length) ordered.push({ faction: null, items: ungrouped })
    return ordered
  }

  // ANU-36: same treatment as renderBountyCard — keep as a plain function so
  // it doesn't get reconciled as a fresh component type on every render
  // (which would remount the descendant BountyCard subtree and drop focus).
  const renderFactionGroupedList = (list, dim = false) => {
    if (!isGm || factions.length === 0) {
      return <div style={dim ? { opacity: 0.6 } : undefined}>{list.map(b => renderBountyCard(b))}</div>
    }
    const grouped = groupByFaction(list)
    return (
      <div style={dim ? { opacity: 0.6 } : undefined} className="space-y-3">
        {grouped.map(({ faction, items }) => (
          <div key={faction?.id || 'all'}>
            <div className="flex items-center gap-2 mb-2 pl-1" style={{ borderLeft: `3px solid ${faction?.color || '#4aaa5a'}`, paddingLeft: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: faction?.color || '#4aaa5a', display: 'inline-block' }} />
              <span style={{ ...C.mono, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: faction?.color || '#4aaa5a' }}>
                {faction?.name || 'ALL FACTIONS'} — {items.length}
              </span>
            </div>
            {items.map(b => renderBountyCard(b))}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {activeBounties.length > 0 && (
        <div>
          <SectionHeader label="ACTIVE" count={activeBounties.length} collapsed={activeCollapsed} section="active" />
          {!activeCollapsed && renderFactionGroupedList(activeBounties)}
        </div>
      )}
      {queuedBounties.length > 0 && (
        <div>
          <SectionHeader label="QUEUED" count={queuedBounties.length} collapsed={queuedCollapsed} section="queued" color="#ffcc33" />
          {!queuedCollapsed && renderFactionGroupedList(queuedBounties)}
        </div>
      )}
      {resolvedBounties.length > 0 && (
        <div>
          <SectionHeader label="RESOLVED" count={resolvedBounties.length} collapsed={resolvedCollapsed} section="resolved" color="#2a6a3a" />
          {!resolvedCollapsed && renderFactionGroupedList(resolvedBounties, true)}
        </div>
      )}
      {cancelledBounties.length > 0 && (
        <div>
          <SectionHeader label="CANCELLED" count={cancelledBounties.length} collapsed={cancelledCollapsed} section="cancelled" color="#ff2a2a" />
          {!cancelledCollapsed && renderFactionGroupedList(cancelledBounties, true)}
        </div>
      )}
    </div>
  )
}
